// A prompt as it was written (Poulpe-139): its backtick code drawn as code,
// a fenced block under its language word, and every other word a text node.
// Moved from today's Code tab (code/PromptText.tsx there); a block is drawn
// in one colour, since the new pages have no highlighter yet.
import { useId, useMemo, useState } from 'react'
import { cx, foldPoint } from './bits'
import { promptParts } from './promptCode'

/** A block in the box a reply's has: the language as typed above it. */
function PromptBlock({ code, label }: { code: string; label: string }) {
  return (
    <div className="md-code-block">
      {label && (
        <div className="md-code-head">
          <span className="md-code-lang">{label}</span>
        </div>
      )}
      <pre className="md-code">
        <code>{code}</code>
      </pre>
    </div>
  )
}

/** The prompt uses the same fold point and controls as plain transcript text (Poulpe-139). */
export function PromptText({ text, lines = 14, onFold }: { text: string; lines?: number; onFold?: () => void }) {
  const [open, setOpen] = useState(false)
  const id = useId()
  const cut = useMemo(() => foldPoint(text, lines, lines * 240), [text, lines])
  const total = useMemo(() => (cut < 0 ? 0 : text.split('\n').length - Number(text.endsWith('\n'))), [text, cut])
  const parts = useMemo(() => promptParts(text), [text])
  const folded = cut >= 0 && !open
  const limit = folded ? cut : text.length
  return (
    <div className="code-folded">
      <div id={id} className={cx('code-text code-user-text', folded && 'is-folded')}>
        <div className="markdown-body code-md">
          {parts
            .filter((part) => part.start < limit)
            .map((part) => {
              const words = text.slice(part.from, Math.max(part.from, Math.min(part.to, limit)))
              return part.kind === 'block' ? <PromptBlock key={part.start} code={words} label={part.label} /> : part.kind === 'inline' ? <code key={part.start}>{words}</code> : words
            })}
        </div>
      </div>
      {cut >= 0 && (
        <button
          type="button"
          className="button button-quiet button-small code-more"
          aria-expanded={open}
          aria-controls={id}
          onClick={() => {
            onFold?.()
            setOpen((was) => !was)
          }}
        >
          {open ? 'Show less' : total > lines ? `Show all ${total} lines` : 'Show all'}
        </button>
      )}
    </div>
  )
}
